import type { WidgetId } from './types/panelItem';
import type { PanelId, PanelPlacement, PlacementSource } from './types/panelPlacement';
import type { PanelEligible } from './types/panelPolicy';

// --- Panel policy ---

/** Opts a tool component into the main panel via its static `displayInPanel` flag. */
export const belongsInPanel = (component: PanelEligible | undefined): boolean => component?.displayInPanel === true;

// --- Placement ---

export const DEFAULT_PLACEMENT: PanelPlacement = { strategy: 'shared', role: 'widgets', title: 'Panel' };

export const readAuthoredPanelId = (params: unknown): PanelId | undefined => {
  if (typeof params !== 'object' || params === null || !('panelRef' in params)) {
    return undefined;
  }
  const ref = (params as { panelRef?: unknown }).panelRef;
  return typeof ref === 'string' && ref.length > 0 ? (ref as PanelId) : undefined;
};

/** Precedence: agent-authored `panelRef` → the component's declared placement → default "widgets". */
export const getPlacement = (component: PlacementSource, params: unknown): PanelPlacement => {
  const panelRef = readAuthoredPanelId(params);
  if (panelRef) {
    return { strategy: 'target', panelId: panelRef };
  }
  return component?.panelPlacement ?? DEFAULT_PLACEMENT;
};

/** Deterministic id of the panel a placement resolves to, keyed by role / widget / target. */
export const targetPanelId = (placement: PanelPlacement, item: { id: WidgetId }): PanelId => {
  switch (placement.strategy) {
    case 'shared':
    case 'replace':
      return `role:${placement.role}`;
    case 'dedicated':
      return `item:${item.id}`;
    case 'target':
      return `custom:${placement.panelId}`;
  }
};

/** Deterministic id of the panel that hosts a backend dashboard template. */
export const dashboardPanelId = (templateId: string): PanelId => `custom:template:${templateId}`;
